import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, Loader2, CheckCircle2, Gauge, MessageSquareText } from 'lucide-react';
import { api } from '@/lib/api';
import { tpError } from '@/lib/tpErrors';
import { toast } from 'sonner';
import { PerfProfileView } from '@/components/client/PerfProfileView';

export default function MePerformance() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [ack, setAck] = useState({ status: 'reviewed', comment: '', agree: null });
  const [fbNote, setFbNote] = useState('');

  const load = useCallback(() => {
    api.get('/me/performance').then((r) => setData(r.data)).catch((e) => toast.error(tpError(t, e)));
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const open = async (eid) => {
    try {
      const r = await api.get(`/me/performance/${eid}`);
      setProfile(r.data);
    } catch (e) { toast.error(tpError(t, e)); }
  };

  if (!data) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  if (profile) {
    const isSubject = profile.viewer === 'subject';
    return (
      <div className="space-y-5" data-testid="me-performance-profile">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => { setProfile(null); load(); }} className="gap-1.5" data-testid="me-tp-back">
            <ArrowLeft className="h-4 w-4" />{t('common.back', { defaultValue: 'Back' })}
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{isSubject ? t('talentperformance.me.myProfile', { defaultValue: 'My Performance Profile' }) : profile.snapshot?.user_name}</h3>
            <p className="text-xs text-muted-foreground">{profile.cycle?.name}</p>
          </div>
        </div>
        <Card className="p-5 shadow-card">
          <PerfProfileView results={profile.results} evaluation={profile} />
        </Card>

        {isSubject && (
          <Card className="p-4 shadow-card space-y-2">
            <h4 className="text-sm font-semibold">{t('talentperformance.me.ackTitle', { defaultValue: 'Acknowledgement' })}</h4>
            {profile.acknowledgement?.at ? (
              <div className="text-sm text-success flex items-center gap-1.5" data-testid="me-tp-acked">
                <CheckCircle2 className="h-4 w-4" />
                {t(`talentperformance.ack.${profile.acknowledgement.status}`, { defaultValue: profile.acknowledgement.status })}
                {profile.acknowledgement.comment && <span className="text-muted-foreground"> — {profile.acknowledgement.comment}</span>}
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">{t('talentperformance.me.ackHint', { defaultValue: 'Acknowledging means you received and reviewed these results — it does not require you to agree. You may add comments.' })}</p>
                <Button size="sm" onClick={() => setAckOpen(true)} data-testid="me-tp-ack-btn">
                  {t('talentperformance.me.ackAction', { defaultValue: 'Acknowledge results' })}
                </Button>
              </>
            )}
          </Card>
        )}

        {!isSubject && !profile.feedback?.done && (
          <Card className="p-4 shadow-card space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2"><MessageSquareText className="h-4 w-4" />{t('talentperformance.eval.feedback', { defaultValue: 'Feedback conversation' })}</h4>
            <Textarea rows={2} value={fbNote} onChange={(e) => setFbNote(e.target.value)}
              placeholder={t('talentperformance.eval.feedbackPh', { defaultValue: 'Optional note about the performance conversation…' })} data-testid="me-tp-fb-note" />
            <Button size="sm" disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post(`/me/performance/${profile.id}/feedback`, { note: fbNote || null });
                  toast.success(t('talentperformance.eval.feedbackOk', { defaultValue: 'Feedback recorded.' }));
                  open(profile.id);
                } catch (e) { toast.error(tpError(t, e)); } finally { setBusy(false); }
              }} data-testid="me-tp-fb-btn">
              {t('talentperformance.eval.recordFeedback', { defaultValue: 'Record conversation' })}
            </Button>
          </Card>
        )}
        {!isSubject && profile.feedback?.done && (
          <p className="text-sm text-success flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />{t('talentperformance.eval.feedbackDone', { defaultValue: 'Conversation recorded' })}</p>
        )}

        <Dialog open={ackOpen} onOpenChange={setAckOpen}>
          <DialogContent className="sm:max-w-md" data-testid="me-tp-ack-dialog">
            <DialogHeader>
              <DialogTitle>{t('talentperformance.me.ackAction', { defaultValue: 'Acknowledge results' })}</DialogTitle>
              <DialogDescription>{t('talentperformance.me.ackHint', { defaultValue: 'Acknowledging means you received and reviewed these results — it does not require you to agree.' })}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-2">
                {[true, false, null].map((v, i) => (
                  <Button key={i} type="button" size="sm" variant={ack.agree === v ? 'default' : 'outline'}
                    onClick={() => setAck((a) => ({ ...a, agree: v }))} data-testid={`me-tp-agree-${v}`}>
                    {v === true ? t('talentperformance.ack.agree', { defaultValue: 'I agree' })
                      : v === false ? t('talentperformance.ack.disagree', { defaultValue: 'I disagree' })
                        : t('talentperformance.ack.noPosition', { defaultValue: 'No position' })}
                  </Button>
                ))}
              </div>
              <div className="space-y-1">
                <Label>{t('talentperformance.ack.comment', { defaultValue: 'Employee comment' })} ({t('common.optional', { defaultValue: 'optional' })})</Label>
                <Textarea rows={3} value={ack.comment} onChange={(e) => setAck((a) => ({ ...a, comment: e.target.value }))} data-testid="me-tp-ack-comment" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAckOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button disabled={busy} data-testid="me-tp-ack-submit"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.post(`/me/performance/${profile.id}/acknowledge`, {
                      status: 'reviewed', comment: ack.comment || null, agree: ack.agree,
                    });
                    toast.success(t('talentperformance.me.ackOk', { defaultValue: 'Acknowledgement recorded.' }));
                    setAckOpen(false);
                    open(profile.id);
                  } catch (e) { toast.error(tpError(t, e)); } finally { setBusy(false); }
                }}>
                {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('common.confirm', { defaultValue: 'Confirm' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const List = ({ title, items, testid }) => (
    <Card className="shadow-card overflow-hidden">
      <div className="p-4 border-b"><h4 className="text-sm font-semibold">{title}</h4></div>
      {items.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground text-center">{t('talentperformance.me.empty', { defaultValue: 'Nothing here yet.' })}</p>
      ) : items.map((e) => (
        <button key={e.id} type="button" onClick={() => open(e.id)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b last:border-0 hover:bg-muted/30 text-left"
          data-testid={`${testid}-${e.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{e.kind === 'subject' ? e.cycle_name : e.subject_name}</p>
            <p className="text-xs text-muted-foreground truncate">{e.kind === 'subject' ? '' : e.cycle_name}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {e.overall !== null && e.overall !== undefined && <Badge variant="outline">{e.overall}</Badge>}
            <Badge variant="outline">{t(`talentperformance.evalStatus.${e.status}`, { defaultValue: e.status })}</Badge>
          </div>
        </button>
      ))}
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="me-performance">
      <div className="flex items-center gap-2">
        <Gauge className="h-5 w-5 text-info" />
        <h2 className="text-xl font-semibold">{t('talentperformance.me.title', { defaultValue: 'My Performance' })}</h2>
      </div>
      <List title={t('talentperformance.me.myResults', { defaultValue: 'My results' })} items={data.my_results || []} testid="me-tp-mine" />
      <List title={t('talentperformance.me.myTeam', { defaultValue: 'My team (as supervisor)' })} items={data.my_team || []} testid="me-tp-team" />
    </div>
  );
}
